import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, FileText, Trash2, Upload } from 'lucide-react';
import type { CustomerAttachmentRecord, CustomersGateway } from '@forq/customers-data';

const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

/** Datei als data:-URL lesen -- so wie die Maschinen-Anhänge (kein Storage-Bucket, Inhalt liegt im Datensatz). */
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Die Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : 'Unbekannter Fehler.';
}

/**
 * Kundenakte-Dokumente: hochladen, herunterladen, entfernen. Bewusst dokumentzentriert (Verträge,
 * Schriftverkehr, gescannte Unterlagen) -- Bilder bekommen eine kleine Vorschau, alles andere ein
 * Datei-Icon. Backend ist der geteilte Anhang-Pfad im CustomersGateway (Demo + Supabase).
 */
export function CustomerDocumentsSection({ tenantId, customerId, gateway, canEdit }: {
  readonly tenantId: string;
  readonly customerId: string;
  readonly gateway: CustomersGateway;
  readonly canEdit: boolean;
}) {
  const [attachments, setAttachments] = useState<readonly CustomerAttachmentRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(() => {
    let active = true;
    gateway.listAttachments(tenantId, customerId)
      .then((list) => { if (active) setAttachments(list); })
      .catch(() => { if (active) setAttachments([]); });
    return () => { active = false; };
  }, [gateway, tenantId, customerId]);
  useEffect(() => reload(), [reload]);

  const addFiles = useCallback(async (files: readonly File[]) => {
    if (files.length === 0) return;
    setError(''); setBusy(true);
    try {
      for (const file of files) {
        if (file.size > MAX_ATTACHMENT_BYTES) throw new Error(`„${file.name}" ist größer als 8 MB.`);
        const content = await readFileAsDataUrl(file);
        const kind = file.type.startsWith('image/') ? 'image' : 'document';
        await gateway.addAttachment({
          tenantId, customerId, kind,
          title: file.name || (kind === 'image' ? 'Bild' : 'Dokument'),
          mimeType: file.type || 'application/octet-stream', byteSize: file.size, content,
        });
      }
      reload();
    } catch (uploadError) { setError(messageOf(uploadError)); }
    finally { setBusy(false); }
  }, [gateway, tenantId, customerId, reload]);

  const download = useCallback(async (item: CustomerAttachmentRecord) => {
    setError('');
    try {
      // Der Inhalt kommt in der Liste nicht mit (bei Dokumenten) -- einzeln nachladen.
      const full = await gateway.getAttachment(tenantId, item.id);
      const link = document.createElement('a');
      link.href = full.content; link.download = full.title;
      document.body.appendChild(link); link.click(); link.remove();
    } catch (downloadError) { setError(messageOf(downloadError)); }
  }, [gateway, tenantId]);

  const remove = useCallback(async (id: string) => {
    setError('');
    try { await gateway.removeAttachment(tenantId, id); reload(); }
    catch (removeError) { setError(messageOf(removeError)); }
  }, [gateway, tenantId, reload]);

  return (
    <section className="customers-section">
      <div className="customers-section__heading">
        <div><span className="customers-eyebrow">Kundenakte</span><h3>Dokumente & Dateien</h3></div>
        {canEdit ? (
          <>
            <input
              ref={fileInput} type="file" multiple hidden
              accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={(event) => { const files = [...(event.target.files ?? [])]; event.target.value = ''; void addFiles(files); }}
            />
            <button type="button" className="customers-secondary" disabled={busy} onClick={() => fileInput.current?.click()}>
              <Upload size={15} /> {busy ? 'Lädt hoch …' : 'Dokument hochladen'}
            </button>
          </>
        ) : null}
      </div>
      {error ? <p className="customers-doc-error" role="alert">{error}</p> : null}
      {attachments.length === 0 ? (
        <p className="customers-muted">Noch keine Dokumente hinterlegt. Verträge, Schriftverkehr oder gescannte Unterlagen hier ablegen (max. 8 MB je Datei).</p>
      ) : (
        <ul className="customers-doc-list">
          {attachments.map((item) => (
            <li key={item.id} className="customers-doc">
              <span className="customers-doc__icon">
                {item.kind === 'image' && item.content ? <img src={item.content} alt={item.title} /> : <FileText size={18} />}
              </span>
              <div className="customers-doc__meta">
                <strong>{item.title}</strong>
                <small>{formatBytes(item.byteSize)} · {new Date(item.createdAt).toLocaleDateString('de-DE')}</small>
              </div>
              <div className="customers-doc__actions">
                <button type="button" className="customers-secondary" title="Herunterladen" onClick={() => void download(item)}><Download size={15} /></button>
                {canEdit ? <button type="button" className="customers-secondary is-danger" title="Entfernen" onClick={() => void remove(item.id)}><Trash2 size={15} /></button> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
